























































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import {
  Alert, NoticeClass, AuthStore,
} from '@kasasa/fbase-components/lib';
import {
  KNoticeContainer,
} from '@kasasa/fbase-components';
import { ValidationProduct, CycleReward, Reward } from '@/api/rewardsvalidation';

interface TableItem {
  cycle: string,
  cycleReward: CycleReward,
  children: TableItem[],
  expanded: boolean
}

@Component({ computed: mapState(['fiId']) })
export default class CycleValidationRewardsTable extends Vue {
  @Prop() validationResult!: ValidationProduct;

  @Prop({ default: false }) machineLearningNotFound!: boolean;

  fiId!: string;

  headers: DataTableHeader[] = [];

  items: TableItem[] = [];

  missingRewardRanges: string = '';

  rangesUnpopulated: boolean = false;

  readonly rewardTypeFriendlyNameMap: Map<string, string> = new Map<string, string>([
    ['COMBINED_ATM_FEE_REFUNDS', 'ATM Refunds'],
    ['ATM_TRANSACTION_FEE_REFUNDS', 'ATM Terminal Fee Refunds'],
    ['FOREIGN_ATM_USAGE_FEE_REFUND', 'Internal ATM Fee Refunds'],
    ['TUNES_GRANTS', 'Tunes Grants'],
    ['TUNES_CREDITS', 'Tunes Refunds'],
    ['INTEREST_ADJUSTMENT', 'Interest Adjustment'],
    ['QUALIFIED_INTEREST', 'Qualified Interest'],
    ['NONQUALIFIED_INTEREST', 'Non-Qualified Interest'],
    ['CASH_BACK', 'Cash Back'],
    ['QUAL_INTEREST_COUNT', '# Qualified Accounts'],
    ['NONQUAL_INTEREST_COUNT', '# Non-Qualified Accounts'],
    ['SERVICE_FEE', 'Service Fee'],
    ['EATS_REWARD', 'Eats Reward'],
    ['STREAMING_REWARD', 'Streaming Reward'],
  ]);

  readonly INVALID_CLASS = 'invalidValue'

  readonly VALID_CLASS = 'validValue'

  readonly WARNING_CLASS = 'warningValue'

  mounted() {
    this.buildTable();
  }

  @Watch('validationResult')
  buildTable() {
    this.checkRanges();
    this.buildHeaders();
    this.buildItems();
  }

  checkRanges() {
    this.missingRewardRanges = '';
    // Check if validation ranges have been populated for the current cycle.  If they have, check for any missing rewards.
    // If it looks like ranges are undefined, then it's a legacy snapshot and we fall back to using the validation flags.
    if (!this.machineLearningNotFound && this.validationResult && this.validationResult.cycleRewards) {
      if (this.validationResult.cycleRewards.current.rewards.length > 0 && this.validationResult.cycleRewards.current.rewards[0].yellowThresholdMinimum === undefined) {
        this.rangesUnpopulated = true;
      } else {
        this.rangesUnpopulated = false;
        // Check for missing ranges
        this.validationResult.cycleRewards.current.rewards.forEach((reward: Reward) => {
          if (!reward.yellowThresholdMinimum) {
            let rewardName: string | undefined = this.rewardTypeFriendlyNameMap.get(reward.rewardType);
            if (!rewardName) {
              rewardName = '?';
            }
            if (this.missingRewardRanges.length > 0) {
              this.missingRewardRanges += ', ';
            }
            this.missingRewardRanges += rewardName;
          }
        });
      }
    }
  }

  buildHeaders() {
    this.headers = [];

    if (this.validationResult && this.validationResult.cycleRewards) {
      const addedRewards = new Set<string>();
      this.buildHeadersForCycleReward(this.validationResult.cycleRewards.average, addedRewards);
      this.buildHeadersForCycleReward(this.validationResult.cycleRewards.current, addedRewards);
      this.buildHeadersForCycleReward(this.validationResult.cycleRewards.currentMinusOne, addedRewards);
      this.buildHeadersForCycleReward(this.validationResult.cycleRewards.currentMinusTwo, addedRewards);
      this.buildHeadersForCycleReward(this.validationResult.cycleRewards.currentMinusThree, addedRewards);

      this.headers.sort((a: DataTableHeader, b: DataTableHeader): number => a.text.localeCompare(b.text));

      this.headers.splice(0, 0, { text: '', value: 'toggle', width: '1px' });
      this.headers.splice(1, 0, { text: 'Cycle', value: 'cycle', width: '200px' });

      this.headers.push({ text: '', value: '' }); // Blank column to take up remaining table width
    }
  }

  buildHeadersForCycleReward(cycleReward: CycleReward | null, addedRewards: Set<string>) {
    if (cycleReward && cycleReward.rewards) {
      cycleReward.rewards.forEach((reward: Reward) => {
        if (!addedRewards.has(reward.rewardType)) {
          const friendlyName: string | undefined = this.rewardTypeFriendlyNameMap.get(reward.rewardType);

          this.headers.push({
            text: friendlyName || '?',
            value: reward.rewardType,
            width: '200px',
          });

          addedRewards.add(reward.rewardType);
        }
      });
    }
  }

  async buildItems() {
    this.items = [];
    const newItems: TableItem[] = [];

    if (this.validationResult && this.validationResult.cycleRewards) {
      newItems.push({
        cycle: 'Current',
        cycleReward: this.validationResult.cycleRewards.current,
        children: [],
        expanded: false,
      });

      if (this.validationResult.cycleQualifiers.average) {
        const averageItem: TableItem = {
          cycle: '3-Month Average',
          cycleReward: this.validationResult.cycleRewards.average,
          children: [],
          expanded: false,
        };

        if (this.validationResult.cycleRewards.currentMinusOne) {
          averageItem.children.push(CycleValidationRewardsTable.createItemForPreviousCycleReward(this.validationResult.cycleRewards.currentMinusOne));
        }

        if (this.validationResult.cycleRewards.currentMinusTwo) {
          averageItem.children.push(CycleValidationRewardsTable.createItemForPreviousCycleReward(this.validationResult.cycleRewards.currentMinusTwo));
        }

        if (this.validationResult.cycleRewards.currentMinusThree) {
          averageItem.children.push(CycleValidationRewardsTable.createItemForPreviousCycleReward(this.validationResult.cycleRewards.currentMinusThree));
        }

        newItems.push(averageItem);
        this.items = newItems;
      }
    }
  }

  static createItemForPreviousCycleReward(cycleReward: CycleReward): TableItem {
    return {
      cycle: cycleReward.dropDate ? new Date(cycleReward.dropDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '',
      cycleReward,
      children: [],
      expanded: false,
    };
  }

  getClass(item: TableItem, header: DataTableHeader): string {
    let className = '';
    if (header.value !== 'cycle') {
      className = this.VALID_CLASS;
      if (item.cycle === 'Current') {
        const reward: Reward | null = CycleValidationRewardsTable.getReward(item.cycleReward, header.value);
        if (reward) {
          className = this.getClassFromValidation(reward.totalAmountBigDecimal, reward.yellowThresholdMinimum, reward.greenThresholdMinimum,
            reward.greenThresholdMaximum, reward.yellowThresholdMaximum, reward.isValid);
        }
      }
    }
    return className;
  }

  // Thresholds will be undefined for legacy snapshots that did not support importing them from machine-learning.
  // Null for newer snapshots if the file could not be found.
  getClassFromValidation(value: number, yellowMin: number | null | undefined, greenMin: number | null | undefined, greenMax: number | null | undefined,
    yellowMax: number | null | undefined, legacyValidationFlag: boolean | undefined): string {
    let className = this.VALID_CLASS;
    if (yellowMin && greenMin && greenMax && yellowMax) {
      if (value < greenMin) {
        className = value < yellowMin ? this.INVALID_CLASS : this.WARNING_CLASS;
      } else if (value > greenMax) {
        className = value > yellowMax ? this.INVALID_CLASS : this.WARNING_CLASS;
      }
    } else if (this.rangesUnpopulated && legacyValidationFlag === false) {
      className = this.INVALID_CLASS;
    }

    return className;
  }

  getTableValue = (item: TableItem, header: DataTableHeader): string => {
    if (header.value === 'cycle') {
      return item.cycle;
    }
    return CycleValidationRewardsTable.getRewardValue(item.cycleReward, header.value);
  }

  static getRewardValue(cycleReward: CycleReward, rewardType: string): string {
    let value = '';
    const reward: Reward | null = CycleValidationRewardsTable.getReward(cycleReward, rewardType);
    if (reward) {
      if (!rewardType.endsWith('_COUNT')) {
        value = '$';
      }
      value += `${reward.totalAmount}`;
    }

    return value;
  }

  static getReward(cycleReward: CycleReward, rewardType: string): Reward | null {
    for (let i = 0; i < cycleReward.rewards.length; i++) {
      if (cycleReward.rewards[i].rewardType === rewardType) {
        return cycleReward.rewards[i];
      }
    }
    return null;
  }
}
